import cn from 'classnames';
import { omit } from 'lodash-es';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import {
  transcriptorApi,
  type Document,
  type DocumentAnswers,
} from '@/shared/api/transcriptor';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { Button, ChipGroup, toastCaller } from '@/shared/ui';

import { DiagnocoinNotice } from '@/entities/billing';
import { useTranscriptorStartAvailability } from '@/entities/composed';
import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';
import {
  isBlockNoteTemplate,
  openTemplateEditor,
  templatesModel,
  type DocumentTemplate,
  type EncounterKind,
} from '@/entities/templates';
import {
  getRecordingPath,
  transcriptorModel,
  TRANSCRIPTOR_LANGUAGE,
} from '@/entities/transcriptor';

import {
  getStoredOrganizationRecordingMode,
  liveSession,
  saveOrganizationRecordingMode,
  useFileUpload,
  useStartRecording,
  type MicrophonePermissions,
  type RecordingMode,
} from '@/features/audioRecording';

import {
  useRecordingCapabilities,
  useCanEditTemplates,
  useNoteLists,
  useSavePresetTemplate,
  useTabReturn,
  useTreatmentWorkingCopies,
  useUnsavedChangesGuard,
} from '../hooks';
import {
  buildPresetNoteRequest,
  buildPresetTemplate,
  findStructureIssues,
  getDraftNotePresets,
  getSchemaDocuments,
  getTemplateOrigin,
  hasPresetAnswersIn,
  holdsPresetTemplateChanges,
  holdsSectionEdits,
  isPresetNoteRequestEmpty,
  keepPresetValues,
  pushBlockPresets,
  pushPresetAnswers,
  pushPresetLineOrders,
  setDraftNotePresets,
  toSectionOverrides,
  withFieldListKeys,
  type PresetAnswersResult,
  type PushedBlockPresets,
  type SchemaDocument,
} from '../lib';

import { DocumentTabs } from './DocumentTabs';
import { EditTemplateButton } from './EditTemplateButton';
import { InlineConfirm } from './InlineConfirm';
import { MicrophoneSetting } from './MicrophoneSetting';
import { NoteDocument } from './NoteDocument';
import { RecordDropZone } from './RecordDropZone';
import { TemplateLoader, TemplatePreview } from './TemplatePreview';
import styles from './TemplateStartWorkspace.module.scss';
import { WorkspaceLayout } from './WorkspaceLayout';
import { CatalogOriginLabel } from './WorkspaceTemplateCatalog';

// The template card a recording starts from.
export type StartTemplate = {
  kind: EncounterKind;
  name: string;
  schemaId: string;
  // The encounter starts with this protocol or block note.
  templateSeriesId?: string;
  // Selected on demand; a protocol linked always comes with every encounter.
  onDemand: boolean;
};

export type StartMicrophone = MicrophonePermissions & {
  // The level meter holds its own stream; it stops before the recording.
  stopMonitoring: () => void;
};

type Props = {
  patientId: string;
  template: StartTemplate;
  language: string;
  microphone: StartMicrophone;
  className?: string;
  onBack: () => void;
};

// The chosen template before its recording — the note to fill with the
// values known before the start, beside the documents the schema always
// generates — with the recording or the upload on the right. "Edit template"
// opens its template in the editor in another tab; coming back reads it again.
export const TemplateStartWorkspace: FC<Props> = ({
  patientId,
  template,
  language,
  microphone,
  className,
  onBack,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const [file, setFile] = useState<File | null>(null);
  const [starting, setStarting] = useState(false);
  const [documentKey, setDocumentKey] = useState('');
  const [chosenMode, setChosenMode] = useState<RecordingMode>();
  // "Templates" asks first while the start holds values or changes of the
  // template that leaving drops; a navigation away asks the same.
  const [confirmingBack, setConfirmingBack] = useState(false);
  // The versions replaced in the editor whose changes here were kept.
  const [keptVersions, setKeptVersions] = useState('');
  // The summary's and the evaluation's titles and instructions typed for
  // this recording, by template series, each on the version it was typed on.
  const [sectionEdits, setSectionEdits] = useState<
    Record<string, DocumentTemplate>
  >({});
  const schemas = useAppSelector(
    templatesModel.selectors.selectPublishedEncounterSchemas,
  );
  const templates = useAppSelector(
    templatesModel.selectors.selectActiveDocumentTemplates,
  );
  const versions = useAppSelector(
    templatesModel.selectors.selectDocumentTemplatesById,
  );
  const { CanEditPatient, CanRequestReport } = useAppSelector(
    patientModel.selectors.selectPatientYourPermissions(patientId),
  );
  const { funding, canStartRecording, isStartRecordingDisabled } =
    useTranscriptorStartAvailability();
  const recordingBusy = useAppSelector(
    ({ transcriptor: { recording } }) =>
      recording.isRecording ||
      recording.isUploading ||
      recording.isProcessing ||
      recording.isInterrupted,
  );
  const organizationId = useAppSelector(
    organizationModel.selectors.selectRelevantOrganizationID,
  );
  const v2Enabled =
    useAppSelector((state) =>
      organizationModel.selectors.selectTranscriptorV2Enabled(
        state,
        organizationId,
      ),
    ) === true;
  const dictationOffered =
    useRecordingCapabilities(organizationId, v2Enabled) !== false;
  const workingCopies = useTreatmentWorkingCopies({ templates, versions });
  const seriesId = template.templateSeriesId;
  const summary = templates.find(
    (candidate) => candidate.series_id === seriesId,
  );
  const copy =
    seriesId && workingCopies.selectedIds.includes(seriesId)
      ? workingCopies.getCopy(seriesId)
      : undefined;
  const blockNote = !!summary && isBlockNoteTemplate(summary);
  const noteLists = useNoteLists(blockNote ? copy?.draft.template : undefined);
  const schema = schemas.find((item) => item.id === template.schemaId);
  const onDemandSeriesIds = seriesId && template.onDemand ? [seriesId] : [];
  // A treatment's note stands alone; a consultation's documents generated
  // after Stop are shown beside its note.
  const generatedDocuments =
    seriesId && template.kind !== 'consultation'
      ? []
      : getSchemaDocuments(schema, templates).filter(
          ({ reference, template: item }) =>
            reference.inclusion !== 'on_demand' && item.series_id !== seriesId,
        );
  const noteKey = seriesId ? `note:${seriesId}` : undefined;
  const tabKeys = [
    ...(noteKey ? [noteKey] : []),
    ...generatedDocuments.map(({ template: item }) => item.series_id),
  ];
  const activeKey = tabKeys.includes(documentKey)
    ? documentKey
    : tabKeys[0] ?? '';
  const shownDocument = generatedDocuments.find(
    ({ template: item }) => item.series_id === activeKey,
  );
  const editedSections = generatedDocuments.flatMap(({ template: item }) => {
    const edited = sectionEdits[item.series_id];
    const version = edited && versions[edited.id];
    return edited && version && holdsSectionEdits(edited, version)
      ? [{ templateId: edited.id, edited }]
      : [];
  });
  const sectionsRefused = editedSections.some(
    ({ edited }) => findStructureIssues(edited).length > 0,
  );
  const canEditTemplates = useCanEditTemplates();
  const presetTemplate = useSavePresetTemplate();
  // A partner template's structure comes from the partner's template.
  const canSaveAsMine =
    blockNote && canEditTemplates && !!copy && !copy.source.partner_source;
  const canSaveFieldsOf = (templateId: string) =>
    canEditTemplates && !versions[templateId]?.partner_source;
  // Values chosen are this recording's; only a change of the template itself
  // is offered to be saved as the clinic's.
  const noteTemplateChanged =
    blockNote &&
    !!copy &&
    holdsPresetTemplateChanges({
      source: copy.source,
      template: copy.draft.template,
      presets: getDraftNotePresets(copy.draft),
    });

  useEffect(() => {
    if (seriesId) {
      workingCopies.select(seriesId);
    }
    // The working copy of the chosen protocol is opened once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesId]);

  // A version saved in the editor is the series' current one once the
  // clinic's templates are read again; what was set here on an older one is
  // replaced only after asking.
  const replacedNoteVersion =
    copy && summary && summary.version > copy.source.version
      ? summary.id
      : undefined;
  const replacedSections = Object.entries(sectionEdits).flatMap(
    ([series, edited]) => {
      const current = templates.find((item) => item.series_id === series);
      return current && current.version > edited.version
        ? [{ series, edited }]
        : [];
    },
  );
  const replacedVersions = [
    replacedNoteVersion,
    ...replacedSections.map(({ edited }) => edited.id),
  ]
    .filter(Boolean)
    .join(',');
  const replacedHoldsChanges =
    (!!replacedNoteVersion &&
      !!seriesId &&
      !workingCopies.isPristine(seriesId)) ||
    replacedSections.some(({ edited }) => {
      const version = versions[edited.id];
      return !!version && holdsSectionEdits(edited, version);
    });

  const followNewVersions = async () => {
    setSectionEdits((current) =>
      omit(
        current,
        replacedSections.map(({ series }) => series),
      ),
    );
    if (!replacedNoteVersion || !seriesId) {
      return;
    }
    const version =
      versions[replacedNoteVersion] ??
      (await dispatch(
        templatesModel.fetchDocumentTemplateById({
          id: replacedNoteVersion,
          organizationId,
        }),
      )
        .unwrap()
        .catch(() => undefined));
    if (version) {
      workingCopies.restart(seriesId, version);
    }
  };

  useEffect(() => {
    if (replacedVersions && !replacedHoldsChanges) {
      void followNewVersions();
    }
    // Follows each replaced set of versions once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replacedVersions, replacedHoldsChanges]);

  useTabReturn(() => {
    if (organizationId) {
      void dispatch(
        templatesModel.fetchDocumentTemplates({
          organization_id: organizationId,
        }),
      );
    }
  });

  const recordingMode: RecordingMode =
    template.kind === 'treatment' && dictationOffered
      ? chosenMode ?? getStoredOrganizationRecordingMode(organizationId)
      : 'visit';
  const chooseRecordingMode = (mode: RecordingMode) => {
    setChosenMode(mode);
    if (organizationId) {
      saveOrganizationRecordingMode(organizationId, mode);
    }
  };

  const warn = (message: string) =>
    toastCaller({ type: 'warning', message, autoClose: 10000 });

  // The summary's and the evaluation's titles and instructions typed before
  // the start hold for this encounter only, on the versions it pinned.
  const pushSectionEdits = async (
    encounterId: string,
    documents: Document[],
  ) => {
    const pushed = await Promise.all(
      editedSections.map(({ templateId, edited }) => {
        const document = documents.find(
          (item) => item.template_id === templateId,
        );
        return document?.id
          ? transcriptorApi
              .setDocumentSections({
                encounterId,
                documentId: document.id,
                templateId,
                sections: toSectionOverrides(edited),
              })
              .then(
                () => true,
                () => false,
              )
          : false;
      }),
    );
    if (pushed.some((saved) => !saved)) {
      warn(
        formatMessage({
          id: 'workspace.template.sectionsNotSaved',
          defaultMessage:
            'The titles and instructions of the summary or the evaluation set before the start were not saved to this encounter. It is processed with the template’s own.',
        }),
      );
    }
  };

  // The values and note operations set before the start go to the new
  // encounter's protocol, and the fields' titles and instructions to its
  // summary and evaluation. A refused push does not stop the recording.
  const pushPresets = async (encounterId: string) => {
    const presets = copy ? getDraftNotePresets(copy.draft) : undefined;
    const pushesNote =
      !!copy &&
      !!seriesId &&
      !!presets &&
      !(blockNote
        ? isPresetNoteRequestEmpty(
            buildPresetNoteRequest(copy.draft.template, presets),
          )
        : !hasPresetAnswersIn([copy.draft]));
    if (!pushesNote && !editedSections.length) {
      return;
    }
    const loadTemplate = (templateId: string) =>
      dispatch(
        templatesModel.fetchDocumentTemplateById({
          id: templateId,
          organizationId,
        }),
      ).unwrap();
    const receive = (documentId: string, answers: DocumentAnswers | null) => {
      if (
        answers &&
        liveSession.getSnapshot()?.meta.encounterId === encounterId
      ) {
        liveSession.receiveAnswers(documentId, answers);
      }
    };

    const documents = await dispatch(
      transcriptorModel.thunks.fetchConsultationById({
        consultationId: encounterId,
        patientId,
      }),
    )
      .unwrap()
      .then((encounter) => encounter.documents)
      .catch(() => null);
    if (editedSections.length) {
      void pushSectionEdits(encounterId, documents ?? []);
    }
    if (!pushesNote || !copy || !presets) {
      return;
    }

    const stored:
      | (PresetAnswersResult & { pushed?: PushedBlockPresets })
      | null = documents
      ? await (
          blockNote
            ? pushBlockPresets({
                api: transcriptorApi,
                encounterId,
                documents,
                template: copy.draft.template,
                presets,
                loadTemplate,
              }).then((pushed) => ({
                saved: [pushed],
                failed: pushed.complete ? 0 : 1,
                pushed,
              }))
            : pushPresetAnswers({
                api: transcriptorApi,
                encounterId,
                documents,
                drafts: [copy.draft],
                loadTemplate,
              })
        ).catch(() => null)
      : null;
    stored?.saved.forEach(({ documentId, answers }) =>
      receive(documentId, answers),
    );
    if (!stored || stored.failed > 0) {
      warn(
        formatMessage({
          id: 'workspace.template.presetsNotSaved',
          defaultMessage:
            'The values and changes set before the start were not all saved to this encounter. The recording continues and is processed without them.',
        }),
      );
    }

    const pushed = stored?.pushed;
    if (!pushed?.request.laterOrders.length) {
      return;
    }
    await pushPresetLineOrders({
      api: transcriptorApi,
      encounterId,
      documentId: pushed.documentId,
      request: pushed.request,
      stored: pushed.answers,
    })
      .then((answers) => receive(pushed.documentId, answers))
      .catch(() =>
        warn(
          formatMessage({
            id: 'workspace.template.lineOrderNotSaved',
            defaultMessage:
              'The line order set before the start was not applied to this encounter. Drag the lines into place in the note.',
          }),
        ),
      );
  };

  const openRecording = (encounterId: string) =>
    navigate(
      getRecordingPath({ patientID: patientId, recordingID: encounterId }),
    );

  const { startRecording } = useStartRecording({
    patientID: patientId,
    schemaId: template.schemaId,
    selectedOnDemandSeriesIds: onDemandSeriesIds,
    language: language as TRANSCRIPTOR_LANGUAGE,
    selectedDevice: microphone.selectedDevice,
    devices: microphone.devices,
    encounterKind: template.kind,
    recordingMode,
    transcriptorV2: v2Enabled,
    onCleanup: microphone.stopMonitoring,
    onStarted: (encounterId, stream, accepted) => {
      liveSession.begin(
        {
          patientId,
          encounterId,
          kind: template.kind,
          title: template.name,
          canEdit: CanEditPatient,
        },
        template.kind === 'treatment'
          ? { stream, language, mode: accepted.recordingMode }
          : undefined,
      );
      void pushPresets(encounterId);
      openRecording(encounterId);
    },
  });

  const upload = useFileUpload({
    patientId,
    schemaId: template.schemaId,
    selectedOnDemandSeriesIds: onDemandSeriesIds,
    language: language as TRANSCRIPTOR_LANGUAGE,
    onEncounterCreated: pushPresets,
    // The uploaded recording is processed on its page.
    onUploaded: openRecording,
  });

  const busy = starting || upload.isUploading || presetTemplate.saving;
  const canRecord =
    !!schema &&
    canStartRecording &&
    CanRequestReport &&
    !isStartRecordingDisabled &&
    !recordingBusy &&
    !busy &&
    !sectionsRefused;

  const start = async () => {
    if (microphone.hasPermission !== true) {
      await microphone.checkMicrophonePermission();
      return;
    }
    setStarting(true);
    await startRecording();
    setStarting(false);
  };

  // The chosen protocol or block note, whatever the encounter kind, filled
  // with the values known before the start.
  const renderPrefill = () => {
    if (!copy) {
      return summary ? (
        <TemplateLoader key={summary.id} templateId={summary.id} />
      ) : (
        <p className={cn('p3', styles.muted)}>
          <FormattedMessage
            id="workspace.start.templateMissing"
            defaultMessage="This template is not available in the clinic any more."
          />
        </p>
      );
    }
    const { draft } = copy;
    const setDraft = workingCopies.setDraft(draft.template.series_id);
    const sourceLabel = (
      <CatalogOriginLabel origin={getTemplateOrigin(copy.source)} />
    );
    return blockNote ? (
      <NoteDocument
        // A saved version starts the note again, with its own undo.
        key={draft.template.id}
        template={draft.template}
        sourceLabel={sourceLabel}
        disabled={busy}
        prefill={{
          presets: getDraftNotePresets(draft),
          lists: noteLists.lists,
          listsFailed: noteLists.failed,
          onChange: (presets) =>
            setDraft((current) => setDraftNotePresets(current, presets)),
        }}
        templateLink={
          templateLink && (
            <>
              {canSaveAsMine &&
                noteTemplateChanged &&
                renderSaveAsMine(() => void saveNoteAsMine())}
              {templateLink}
            </>
          )
        }
      />
    ) : (
      <NoteDocument
        template={draft.template}
        sourceLabel={sourceLabel}
        disabled={busy}
        options={{
          draft,
          live: false,
          onAnswer: (key, value) =>
            setDraft((current) => ({
              ...current,
              answers: { ...current.answers, [key]: value },
            })),
        }}
      />
    );
  };

  const renderGeneratedDocument = (document: SchemaDocument) => {
    const series = document.template.series_id;
    const templateId = sectionEdits[series]?.id ?? document.template.id;
    const edited = editedSections.find(
      (item) => item.templateId === templateId,
    );
    return (
      <TemplatePreview
        key={templateId}
        templateId={templateId}
        fieldList={{
          disabled: busy,
          actions:
            edited && canSaveFieldsOf(templateId) ? (
              <>
                {renderSaveAsMine(() => void saveFieldsAsMine(edited))}
                {templateLink}
              </>
            ) : (
              templateLink
            ),
          prefill: {
            template: sectionEdits[series],
            onChange: (edited) =>
              setSectionEdits((current) => ({
                ...current,
                [series]: edited,
              })),
          },
        }}
      />
    );
  };

  const renderDocuments = () => {
    if (!tabKeys.length) {
      return null;
    }
    if (!generatedDocuments.length) {
      return renderPrefill();
    }
    return (
      <DocumentTabs
        tabs={[
          ...(noteKey
            ? [
                {
                  key: noteKey,
                  label: formatMessage({
                    id: 'global.note',
                    defaultMessage: 'Note',
                  }),
                  included: true,
                },
              ]
            : []),
          ...generatedDocuments.map(({ template: item }) => ({
            key: item.series_id,
            label: item.tab_title || item.name,
            included: true,
          })),
        ]}
        activeKey={activeKey}
        disabled={false}
        onChange={setDocumentKey}
      >
        {shownDocument
          ? renderGeneratedDocument(shownDocument)
          : renderPrefill()}
      </DocumentTabs>
    );
  };

  const holdsChanges =
    (!!seriesId && !workingCopies.isPristine(seriesId)) ||
    editedSections.length > 0;
  const templateChanged = noteTemplateChanged || editedSections.length > 0;
  const offersSave =
    templateChanged &&
    (!noteTemplateChanged || canSaveAsMine) &&
    editedSections.every(({ templateId }) => canSaveFieldsOf(templateId));
  const leaving = useUnsavedChangesGuard(
    holdsChanges && !starting && !upload.isUploading,
  );
  const confirmingLeave = confirmingBack || leaving.state === 'blocked';
  const leave = () => {
    setConfirmingBack(false);
    if (leaving.state === 'blocked') {
      leaving.proceed();
      return;
    }
    onBack();
  };
  const stay = () => {
    setConfirmingBack(false);
    if (leaving.state === 'blocked') {
      leaving.reset();
    }
  };
  const requestLeave = () =>
    holdsChanges ? setConfirmingBack(true) : onBack();
  // The note then goes on from the saved version with the values chosen on
  // it.
  const saveNoteAsMine = async () => {
    if (!copy || !seriesId) {
      return false;
    }
    const presets = getDraftNotePresets(copy.draft);
    const saved = await presetTemplate.save({
      source: copy.source,
      template: buildPresetTemplate(copy.draft.template, presets),
    });
    if (!saved) {
      return false;
    }
    workingCopies.restart(seriesId, saved, keepPresetValues(saved, presets));
    return true;
  };
  // The saved version, which holds the fields' titles and instructions,
  // takes the place of the one they were typed over; the recording records
  // with it, so nothing is laid over it any more.
  const saveFieldsAsMine = async ({
    templateId,
    edited,
  }: (typeof editedSections)[number]) => {
    const version = versions[templateId];
    if (!version) {
      return false;
    }
    const saved = await presetTemplate.save({
      source: withFieldListKeys(version),
      template: edited,
    });
    if (!saved) {
      return false;
    }
    setSectionEdits((current) => omit(current, edited.series_id));
    return true;
  };
  // From the leave question every changed template is saved in turn, and
  // the leave goes on once all are.
  const saveChangesAndLeave = async () => {
    const savedAll = await editedSections.reduce<Promise<boolean>>(
      async (done, edited) => (await done) && saveFieldsAsMine(edited),
      noteTemplateChanged ? saveNoteAsMine() : Promise.resolve(true),
    );
    if (savedAll) {
      leave();
    }
  };
  const saveAsMineLabel = (
    <FormattedMessage
      id="workspace.start.saveAsMyTemplate"
      defaultMessage="Save as my template"
    />
  );
  const renderSaveAsMine = (onSave: () => void) => (
    <Button
      variant="secondary"
      size="small"
      loading={presetTemplate.saving}
      disabled={busy}
      data-component="workspace-template-save-mine"
      onClick={onSave}
    >
      {saveAsMineLabel}
    </Button>
  );
  const leaveValues = { name: template.name };
  const leaveQuestion = offersSave ? (
    <FormattedMessage
      id="workspace.start.backSaveQuestion"
      defaultMessage="“{name}” was changed before the recording. Save the changes as your template?"
      values={leaveValues}
    />
  ) : templateChanged ? (
    <FormattedMessage
      id="workspace.start.backChangesReset"
      defaultMessage="“{name}” was changed before the recording. The changes will be reset."
      values={leaveValues}
    />
  ) : (
    <FormattedMessage
      id="workspace.start.backValuesReset"
      defaultMessage="The values chosen will be reset."
    />
  );
  const templateLink = canEditTemplates && (
    <EditTemplateButton
      disabled={busy}
      dataComponent="workspace-template-open"
      onClick={() =>
        openTemplateEditor(shownDocument?.template.series_id ?? seriesId)
      }
    />
  );

  const recordingPanel = (
    <>
      <Button
        variant="tertiary"
        size="small"
        icon="arrowDown"
        className={styles.back}
        disabled={busy}
        onClick={requestLeave}
      >
        <FormattedMessage
          id="workspace.start.backToTemplates"
          defaultMessage="Templates"
        />
      </Button>
      {confirmingLeave && (
        <InlineConfirm
          message={leaveQuestion}
          primaryAction={
            offersSave && (
              <Button
                size="small"
                loading={presetTemplate.saving}
                data-component="workspace-start-leave-save"
                onClick={() => void saveChangesAndLeave()}
              >
                {saveAsMineLabel}
              </Button>
            )
          }
          confirmLabel={
            offersSave ? (
              <FormattedMessage
                id="workspace.start.dontSave"
                defaultMessage="Don't save"
              />
            ) : leaving.state === 'blocked' ? (
              <FormattedMessage
                id="workspace.start.leave"
                defaultMessage="Leave"
              />
            ) : (
              <FormattedMessage
                id="workspace.start.goBack"
                defaultMessage="Go back"
              />
            )
          }
          cancelLabel={
            <FormattedMessage id="workspace.start.stay" defaultMessage="Stay" />
          }
          disabled={presetTemplate.saving}
          onConfirm={leave}
          onCancel={stay}
        />
      )}
      {replacedHoldsChanges && replacedVersions !== keptVersions && !busy && (
        <InlineConfirm
          message={
            <FormattedMessage
              id="workspace.start.templateReplaced"
              defaultMessage="“{name}” was changed in the template editor. Load the new version? What was set here before the start will be reset."
              values={leaveValues}
            />
          }
          confirmLabel={
            <FormattedMessage
              id="workspace.start.loadNewVersion"
              defaultMessage="Load the new version"
            />
          }
          cancelLabel={
            <FormattedMessage
              id="workspace.start.keepChanges"
              defaultMessage="Keep my changes"
            />
          }
          onConfirm={() => void followNewVersions()}
          onCancel={() => setKeptVersions(replacedVersions)}
        />
      )}
      <DiagnocoinNotice funding={funding} />
      {template.kind === 'treatment' && (
        <section
          className={styles.mode}
          data-component="workspace-recording-mode"
        >
          <ChipGroup<RecordingMode>
            variant="segmented"
            label={formatMessage({
              id: 'workspace.start.mode',
              defaultMessage: 'Recording mode',
            })}
            items={[
              {
                value: 'visit',
                label: (
                  <FormattedMessage
                    id="workspace.start.mode.visit"
                    defaultMessage="Visit recording"
                  />
                ),
              },
              ...(dictationOffered
                ? [
                    {
                      value: 'dictation' as const,
                      label: (
                        <FormattedMessage
                          id="workspace.start.mode.dictation"
                          defaultMessage="Dictation"
                        />
                      ),
                    },
                  ]
                : []),
            ]}
            value={recordingMode}
            disabled={busy}
            onChange={chooseRecordingMode}
          />
          <p className={cn('p4', styles.muted)}>
            {recordingMode === 'dictation' ? (
              <FormattedMessage
                id="workspace.start.mode.dictationHint"
                defaultMessage="Dictate for up to 5 minutes. After Stop, wait for the final live answer."
              />
            ) : (
              <FormattedMessage
                id="workspace.start.mode.visitHint"
                defaultMessage="The whole visit is recorded; the note is filled after you stop."
              />
            )}
          </p>
        </section>
      )}
      <MicrophoneSetting
        options={microphone.devices.map((device) => ({
          value: device.deviceId,
          label: device.label,
        }))}
        value={microphone.selectedDevice}
        disabled={busy || !microphone.devices.length}
        ready={microphone.hasPermission === true}
        status={
          microphone.hasPermission === true ? (
            <FormattedMessage
              id="workspace.microphoneReady"
              defaultMessage="Microphone ready"
            />
          ) : (
            <FormattedMessage
              id="workspace.enableMicrophone"
              defaultMessage="Microphone access is needed"
            />
          )
        }
        onChange={microphone.setSelectedDevice}
      />
      <div
        className={styles.level}
        role="meter"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(microphone.audioLevel)}
        aria-label={formatMessage({
          id: 'transcriptorRecording.audioLevelLabel',
          defaultMessage: 'Audio Level',
        })}
      >
        <div
          className={styles.levelFill}
          style={{ width: `${Math.min(microphone.audioLevel, 100)}%` }}
        />
      </div>
      <RecordDropZone
        recordable
        disabled={!canRecord}
        file={file}
        starting={starting}
        processing={upload.isUploading}
        startHint={
          recordingBusy ? (
            <FormattedMessage
              id="transcriptorStart.recordingBusy"
              defaultMessage="Another recording is in progress. Finish it in the recording widget first."
            />
          ) : sectionsRefused ? (
            <FormattedMessage
              id="workspace.start.sectionsRefused"
              defaultMessage="Fix the marked fields of the summary or the evaluation to start."
            />
          ) : undefined
        }
        startLabel={
          microphone.hasPermission === true
            ? undefined
            : formatMessage({
                id: 'workspace.enableMicrophone',
                defaultMessage: 'Microphone access is needed',
              })
        }
        onStart={() => void start()}
        onFile={setFile}
        onRemoveFile={() => setFile(null)}
        onProcess={() => {
          if (file) {
            void upload.handleFileUpload(file);
          }
        }}
      />
      {microphone.permissionError && (
        <p role="alert" className={cn('p3', styles.alert)}>
          {microphone.permissionError}
        </p>
      )}
      {upload.isUploading && (
        <p role="status" className={cn('p3', styles.muted)}>
          <FormattedMessage
            id="workspace.uploadProgress"
            defaultMessage="Uploading: {progress}%"
            values={{ progress: upload.uploadProgress }}
          />
        </p>
      )}
      {upload.uploadError && (
        <p role="alert" className={cn('p3', styles.alert)}>
          {upload.uploadError}
        </p>
      )}
    </>
  );

  return (
    <WorkspaceLayout
      className={className}
      recordingPanel={recordingPanel}
      documents={renderDocuments()}
    />
  );
};
